import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { $isAuthenticated, $authLoading } from '~/lib/stores/auth';
import { $showAuthModal } from '~/lib/stores/authModal';
import { capture } from '~/lib/posthog';
import { ANALYTICS_EVENTS } from '@dunea/shared/analytics/events';
import {
  rememberClaimIntent,
  clearClaimIntent,
  clearClaimIntentFor,
  pendingClaimIntent,
} from '~/lib/stores/claimIntent';
import { apiGetClaimPreview, apiClaimGeneration, type ClaimPreview } from '~/lib/api/claims';

/*
 * Public claim landing page (DEV-170): /claim/:token.
 *
 * Mirrors the public shared-preview surface: the captured site fills the
 * viewport (via /p/:chatId?embed=1&static=1), with a persistent claim ribbon
 * pinned to the bottom and a floating CTA card that slides in top-right once
 * the site has rendered. Without a capture, a centered fallback card stands in. Unauthenticated visitors go through the normal auth modal;
 * the token is stashed in localStorage FIRST so the intent survives every auth
 * path, including the magic-link hard-redirect to `/` (ClaimIntentResume
 * navigates back here after sign-in). Terminal states (expired / revoked /
 * already claimed / invalid) each get a readable screen, not a blank error.
 */

const TERMINAL_COPY: Record<Exclude<ClaimPreview['state'], 'valid'>, { title: string; body: string }> = {
  expired: {
    title: 'This claim link has expired',
    body: 'The handoff window has passed. Ask the person who sent it to generate a fresh link.',
  },
  revoked: {
    title: 'This claim link was revoked',
    body: 'The sender withdrew the handoff. If that seems wrong, ask them to share a new link.',
  },
  claimed: {
    title: 'Already claimed',
    body: 'This generation has been claimed into an account. If that was you, it’s waiting in your workspace.',
  },
  invalid: {
    title: 'This claim link isn’t valid',
    body: 'The link may be incomplete or mistyped. Check that you opened the full URL you were sent.',
  },
};

export default function ClaimPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useStore($isAuthenticated);
  const authLoading = useStore($authLoading);

  const [preview, setPreview] = useState<ClaimPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);

  // Floating CTA card (top-right): slides in shortly after the site is on
  // screen so the visitor sees the work first, the pitch second. Dismissible —
  // the bottom ribbon stays as the persistent claim entry point.
  const [cardOpen, setCardOpen] = useState(false);
  const [cardDismissed, setCardDismissed] = useState(false);

  useEffect(() => {
    if (!frameLoaded || cardDismissed) {
      return undefined;
    }

    const t = window.setTimeout(() => setCardOpen(true), 1400);

    return () => window.clearTimeout(t);
  }, [frameLoaded, cardDismissed]);

  const load = useCallback(() => {
    if (!token) {
      setPreview({ state: 'invalid', chatId: null, description: null, hasPreview: false });
      setLoading(false);

      return;
    }

    apiGetClaimPreview(token)
      .then(setPreview)
      .catch(() => setPreview({ state: 'invalid', chatId: null, description: null, hasPreview: false }))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  /* The loop's receiving end. Fires once per resolved link, and only for links
   * that actually resolved, so `state` separates a working share from a dead or
   * already-claimed one. Ratio against preview_shared is the loop's reach; ratio
   * of claim_cta_clicked against this is its conversion. */
  const viewCaptured = useRef(false);

  useEffect(() => {
    if (!preview || viewCaptured.current) {
      return;
    }

    viewCaptured.current = true;
    capture(ANALYTICS_EVENTS.CLAIM_VIEWED, {
      state: preview.state,
      chatId: preview.chatId,
      hasPreview: preview.hasPreview,
    });
  }, [preview]);

  // A dead link means any stashed intent FOR THIS TOKEN is dead too — clear it
  // so the resume hook can't bounce the user back to a terminal screen forever.
  // Scoped to this token: a recipient holding two links must not lose the good
  // one by opening the dead one.
  useEffect(() => {
    if (token && preview && preview.state !== 'valid') {
      clearClaimIntentFor(token);
    }
  }, [token, preview]);

  const claim = useCallback(async () => {
    if (!token) {
      return;
    }

    // `authenticated: false` is the interesting case — that visitor has to sign
    // up before they can claim, which is exactly the loop closing on a new user.
    capture(ANALYTICS_EVENTS.CLAIM_CTA_CLICKED, { authenticated: isAuthenticated, chatId: preview?.chatId ?? null });

    if (!isAuthenticated) {
      // Stash FIRST — the auth flow may leave this page entirely (magic link).
      rememberClaimIntent(token);
      $showAuthModal.set(true);

      return;
    }

    setClaiming(true);

    try {
      const { chatId } = await apiClaimGeneration(token);
      clearClaimIntent();
      toast.success('It’s yours — opening your new project…');
      navigate(`/chat/${chatId}`);
    } catch (err: any) {
      setClaiming(false);
      toast.error(err?.response?.data?.message || 'Could not claim — please try again.');
      // The state may have moved under us (claimed elsewhere, revoked) — re-read.
      load();
    }
  }, [token, isAuthenticated, navigate, load, preview?.chatId]);

  /*
   * Finish the claim the visitor already asked for (DEV-170). Signing up is a
   * detour, not a second decision: whoever pressed the CTA stashed an intent
   * for this token, so once auth lands — via the modal here, or via the
   * magic-link redirect that reloads the app and bounces back — the claim
   * completes on its own instead of demanding the same click twice.
   *
   * Gated on the stashed intent, so an already-signed-in visitor who merely
   * opens the link is NOT claimed into anything: they see the site and choose.
   * The ref keeps it to a single attempt per page load; a failure surfaces its
   * toast and leaves the CTA for a manual retry.
   */
  const autoClaimed = useRef(false);

  useEffect(() => {
    if (authLoading || !isAuthenticated || claiming || autoClaimed.current) {
      return;
    }

    if (!token || preview?.state !== 'valid' || pendingClaimIntent() !== token) {
      return;
    }

    autoClaimed.current = true;
    claim();
  }, [authLoading, isAuthenticated, claiming, token, preview, claim]);

  // static=1: always embed the captured build, never the live sandbox — a live
  // pod's dev HTML would boot the Dunea client inside the frame (see
  // preview.controller). The capture is the stable handoff showcase.
  const previewSrc = preview?.state === 'valid' && preview.chatId ? `/p/${preview.chatId}/?embed=1&static=1` : null;

  const copy = preview && preview.state !== 'valid' ? TERMINAL_COPY[preview.state] : null;
  const siteName = preview?.description || 'Your website';
  const ctaLabel = isAuthenticated ? 'Claim this site' : 'Sign in & claim this site';

  // `compact` shortens the label on phones so the site name keeps room in the ribbon.
  const claimButton = (extra = '', compact = false) => (
    <button
      type="button"
      onClick={claim}
      disabled={claiming}
      className={`ui-btn-accent justify-center whitespace-nowrap text-[13px] font-semibold ${extra}`}
    >
      {claiming ? (
        <span className="i-svg-spinners:90-ring-with-bg text-[16px]" />
      ) : (
        <>
          {compact ? (
            <>
              <span className="sm:hidden">{isAuthenticated ? 'Claim' : 'Sign in & claim'}</span>
              <span className="hidden sm:inline">{ctaLabel}</span>
            </>
          ) : (
            ctaLabel
          )}
          <span className="i-ph:arrow-right-bold text-[13px]" />
        </>
      )}
    </button>
  );

  // ── Valid claim with a captured site: full-viewport shared-preview layout ──
  if (!loading && !authLoading && preview?.state === 'valid' && preview.hasPreview && previewSrc) {
    return (
      <div className="ui-root fixed inset-0 bg-page">
        {!frameLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-page">
            <div className="i-svg-spinners:90-ring-with-bg text-2xl text-muted" />
          </div>
        )}
        {/* Site fills the viewport; bottom padding keeps page content clear of the ribbon. */}
        <iframe
          src={previewSrc}
          title="Site preview"
          onLoad={() => setFrameLoaded(true)}
          className="absolute inset-x-0 top-0 w-full border-0"
          style={{ height: 'calc(100% - 56px - env(safe-area-inset-bottom))' }}
        />

        {/* Floating CTA card — top-right, slides in once the site is on screen. */}
        <AnimatePresence>
          {cardOpen && !cardDismissed && (
            <motion.div
              initial={{ opacity: 0, y: -18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-4 bottom-[calc(68px+env(safe-area-inset-bottom))] z-20 w-[min(320px,calc(100vw-32px))] sm:bottom-auto sm:top-4 rounded-xl border border-subtle bg-raised/95 p-4 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] backdrop-blur-[10px]"
            >
              <button
                type="button"
                onClick={() => setCardDismissed(true)}
                aria-label="Dismiss"
                className="ui-btn-icon absolute right-2 top-2 touch:right-1 touch:top-1"
              >
                <span className="i-ph:x text-[12px]" />
              </button>

              <div className="mb-2 inline-flex items-center gap-1.5 font-geist-mono text-[9.5px] uppercase tracking-[0.14em] text-accent">
                <span className="i-ph:gift-fill text-[11px]" /> Built for you
              </div>

              <h2 className="ui-serif m-0 mb-1.5 text-[20px] leading-[1.15] text-primary">
                {siteName} is <em className="italic text-accent">ready to claim</em>
              </h2>

              <p className="m-0 mb-3.5 text-[11.5px] leading-[1.5] text-secondary">
                Move it into your own account — chat, code, and live site included.
              </p>

              {claimButton('w-full py-2 text-[12.5px]')}

              <p className="m-0 mt-2 text-[10.5px] leading-[1.45] text-muted">
                {isAuthenticated
                  ? 'This link stops working once it’s claimed.'
                  : 'New here? Signing up takes a minute — the project lands in your new account.'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Persistent claim ribbon — the shared-preview banner, but actionable. */}
        <div className="fixed inset-x-0 bottom-0 z-10 flex h-[calc(56px+env(safe-area-inset-bottom))] items-center gap-3 border-t border-hairline bg-page/90 px-4 pb-[env(safe-area-inset-bottom)] backdrop-blur-[8px]">
          <Link to="/" className="flex shrink-0 items-center gap-2 no-underline">
            <img src="/dunea-mark.svg" alt="" className="h-5 w-5" />
            <span className="hidden font-geist text-[13px] font-semibold tracking-[-0.01em] text-primary sm:inline">
              Dunea
            </span>
          </Link>
          <span className="hidden h-4 w-px bg-hairline sm:block" />
          <span className="min-w-0 flex-1 truncate text-[12.5px] text-secondary">
            <span className="i-ph:gift-fill mr-1.5 align-[-2px] text-[13px] text-accent" />
            <span className="text-primary">{siteName}</span>
            <span className="hidden sm:inline"> was built for you — claim it to make it yours.</span>
          </span>
          {claimButton('shrink-0 px-4 py-2', true)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full bg-page transition-theme overflow-y-auto">
      <div className="mx-auto flex min-h-dvh w-[min(880px,100%)] flex-col items-center justify-center px-5 py-12">
        {/* Wordmark — this page is often a visitor's first Dunea screen. */}
        <Link to="/" className="mb-8 flex items-center gap-2 no-underline">
          <img src="/dunea-mark.svg" alt="" className="h-6 w-6" />
          <span className="font-geist text-[15px] font-semibold tracking-[-0.01em] text-primary">Dunea</span>
        </Link>

        {loading || authLoading ? (
          <div className="i-svg-spinners:90-ring-with-bg text-2xl text-muted" />
        ) : preview?.state === 'valid' ? (
          <>
            <div className="mb-2 inline-flex items-center gap-2 font-geist-mono text-[11px] uppercase tracking-[0.15em] text-accent">
              <span className="i-ph:gift-fill text-[12px]" /> A site built for you
            </div>

            <h1 className="ui-serif m-0 mb-3 break-words text-center text-[28px] leading-[1.08] text-primary sm:text-[38px]">
              {siteName} is <em className="italic text-accent">ready to claim</em>
            </h1>

            <p className="mb-7 max-w-[46ch] text-center text-[14px] leading-[1.55] text-secondary">
              Claim it to move this project into your own account — the chat, the code, and the live site all come with
              it, yours to keep building.
            </p>

            {/* No captured site to showcase — fallback card. */}
            <div className="mb-8 flex w-full flex-col items-center justify-center gap-3 rounded-2xl border border-hairline bg-surface py-16">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-hairline bg-raised">
                <div className="i-ph:globe-simple text-2xl text-muted" />
              </div>
              <p className="m-0 text-[13px] text-muted">{siteName} — preview opens once it’s yours.</p>
            </div>

            {claimButton('w-full max-w-[360px] py-3 text-[14px]')}

            <p className="mt-3.5 max-w-[44ch] text-center text-[12px] leading-[1.5] text-muted">
              {isAuthenticated
                ? 'The project moves into your account — this link stops working afterwards.'
                : 'New here? Signing up takes a minute, and the project lands straight in your new account.'}
            </p>
          </>
        ) : (
          (() => {
            const c = copy ?? TERMINAL_COPY.invalid;

            return (
              <div className="max-w-[420px] text-center">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-hairline bg-raised">
                  <div className="i-ph:link-break text-2xl text-muted" />
                </div>
                <h1 className="m-0 mb-2.5 text-[22px] font-semibold text-primary">{c.title}</h1>
                <p className="m-0 mb-7 text-[14px] leading-[1.6] text-secondary">{c.body}</p>
                <Link to="/" className="ui-btn-accent inline-flex px-6 py-2.5 text-[13.5px] font-semibold no-underline">
                  Go to Dunea
                </Link>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}
